import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import SportSwitcher from "@/components/SportSwitcher";
import { FileText, Sparkles } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const { sport } = useSport();
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get(`/reports/${user.id}?sport=${sport}`).then((r) => setReports(r.data));
  }, [user, sport]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">REPORTS</div>
          <h1 className="h-display text-5xl tracking-tighter">4-week development reports.</h1>
        </div>
        <SportSwitcher />
      </div>

      {!reports.length && (
        <div className="card-surface p-12 text-center fade-up d1">
          <FileText className="w-12 h-12 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
          <div className="h-display text-2xl mb-2">No {SPORT_META[sport].label.toLowerCase()} reports yet</div>
          <div className="text-sm text-[#A3A3A3] max-w-md mx-auto">Your coach will generate a 4-week AI-powered review of your strengths, weaknesses and focus areas after enough sessions.</div>
        </div>
      )}

      <div className="space-y-4 fade-up d1">
        {reports.map((r) => {
          const m = SPORT_META[r.sport] || SPORT_META.squash;
          return (
            <div key={r.id} className="card-surface p-6" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4" style={{ color: m.color }} />
                    <div className="label-mono" style={{ color: m.color }}>{m.label.toUpperCase()} · AI GENERATED</div>
                  </div>
                  <h2 className="h-display text-2xl tracking-tight">Report · {new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</h2>
                </div>
                <div className="label-mono">{r.sessions_count} sessions analysed</div>
              </div>
              <div className="grid md:grid-cols-2 gap-5 text-sm">
                <Block title="Strengths" body={r.strengths} color={m.color} />
                <Block title="Weaknesses" body={r.weaknesses} color="#FF4500" />
                <Block title="Next focus" body={r.focus} color="#007AFF" />
                <Block title="Summary" body={r.summary} color="#A3A3A3" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Block({ title, body, color }) {
  return (
    <div className="border-l-2 pl-4" style={{ borderColor: color }}>
      <div className="label-mono mb-1" style={{ color }}>{title}</div>
      <div className="text-[#E5E5E5] leading-relaxed">{body || "—"}</div>
    </div>
  );
}
